import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stack,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useFormik } from "formik";
import { CustodianName } from "./Schema";
import { useSelector } from "react-redux";
import { selectCustodians } from "../../Redux/Reducers/selectors";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const CustodianDialog = ({ open, onClose, onSave, custodian }) => {
  const theme = useTheme();
  const custodians = useSelector(selectCustodians);
  const isEdit = !!custodian;

  const initialValues = {
    name: custodian?.name || "",
    abbreviation: custodian?.abbreviation || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: CustodianName,
    enableReinitialize: true,
    onSubmit: (values) => {
      const abbreviation = values.abbreviation.toLowerCase();
      
      // Check for duplicate abbreviation
      const existingCustodian = custodians?.find((c) => {
        if (isEdit) {
          return c.custodianId !== custodian.custodianId && c.abbreviation === abbreviation;
        }
        return c.abbreviation === abbreviation;
      });

      if (existingCustodian) {
        alert(`${abbreviation} already exists, try a new one.`);
        return;
      }

      onSave({
        ...values,
        abbreviation,
      });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(16,30,54,0.10)',
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
              <AccountBalanceIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: 22, fontFamily, lineHeight: 1.2 }}
              >
                {isEdit ? "Edit Custodian" : "Add Custodian"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, fontFamily, mt: 0.5 }}
              >
                {isEdit ? "Update custodian details below." : "Enter custodian details to add a new custodian."}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Full Name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            InputLabelProps={{ shrink: true, sx: { fontWeight: 600, fontFamily } }}
            inputProps={{ style: { fontFamily } }}
            variant="outlined"
          />
          <TextField
            label="Abbreviation"
            name="abbreviation"
            value={formik.values.abbreviation}
            disabled={isEdit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.abbreviation && Boolean(formik.errors.abbreviation)}
            helperText={formik.touched.abbreviation && formik.errors.abbreviation}
            fullWidth
            InputLabelProps={{ shrink: true, sx: { fontWeight: 600, fontFamily } }}
            inputProps={{ style: { fontFamily } }}
            variant="outlined"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 1, fontWeight: 600, fontFamily, textTransform: 'none', px: 3, minWidth: 120 }}
        >
          {isEdit ? "Save" : "Add Custodian"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustodianDialog; 